import { formatIncomingDate } from "./incomingPatientDates";

const text = (value) => String(value == null ? "" : value).trim();
const customerKey = (value) => text(value).normalize("NFKC").toLowerCase().replace(/[\s_-]/g, "");
// Keep these rules aligned with functions/incomingBookingSync.js.
const bookingPolicies = [
  { customer: "CHG03", label: "CHG 3", orderPrefix: "CHG-", maxDaily: 6, weekdays: null,
    aliases: ["chg3", "chg03", "chularat3internationalhospital"] },
  { customer: "CHG11", label: "CHG 11", orderPrefix: "CHG11-", maxDaily: 3, weekdays: [4, 5, 6],
    aliases: ["chg11", "chularat11internationalhospital"] },
];

export const getIncomingBookingPolicy = (value) => bookingPolicies.find((policy) =>
  policy.aliases.includes(customerKey(value))) || null;

export const isChg3Customer = (value) => {
  const policy = getIncomingBookingPolicy(value);
  return Boolean(policy && policy.customer === "CHG03");
};

export const isIncomingBookingDayAllowed = (day, customer) => {
  const policy = getIncomingBookingPolicy(customer);
  const date = new Date(`${day}T00:00:00Z`);
  return Boolean(policy && /^\d{4}-\d{2}-\d{2}$/.test(day) && Number.isFinite(date.getTime()) &&
    date.toISOString().slice(0, 10) === day && (!policy.weekdays || policy.weekdays.includes(date.getUTCDay())));
};

export const incomingBookingErrorMessage = (error, day, customer = "CHG03") => {
  const reason = error && error.details && error.details.reason;
  const policy = getIncomingBookingPolicy(customer);
  const label = policy ? policy.customer : customer;
  if (["incoming-dob-required", "incoming-dob-invalid", "incoming-dob-conflict"].includes(reason)) return error.message;
  if (reason === "booking-status-locked" || reason === "booking-scope-conflict") {
    const latest = formatIncomingDate(error.details.latestSleepStudyDate);
    const status = error.details.bookingStatus || "ไม่ระบุสถานะ";
    const cause = reason === "booking-status-locked" ? `Booking ล่าสุดมีสถานะ ${status} เปลี่ยนวันที่ได้เฉพาะ Sleep Test Scheduled` : "Booking ล่าสุดไม่ใช่รอบจองของ Node/Customer ที่เลือก";
    return `${cause}\nไม่ได้แก้ไข Booking และคืนวันที่ Sleep Study ใน Incoming เป็น ${latest} แล้ว กรุณาตรวจสอบ Booking Calendar`;
  }
  if (reason === "booking-patient-ambiguous") {
    return "พบผู้ป่วยชื่อเดียวกันหลาย HN ยังไม่ได้บันทึกข้อมูล กรุณาเชื่อมโยง Company HN ก่อนเปลี่ยนวันที่";
  }
  if (reason === "booking-full") {
    return `วันที่ ${formatIncomingDate(day)} ไม่มีที่ว่างสำหรับ ${label} แล้ว\nยังไม่ได้บันทึกข้อมูล กรุณาเลือกวันที่มีห้องว่างแล้วกดบันทึกอีกครั้ง`;
  }
  if (reason === "booking-day-not-allowed") {
    return "CHG11 รับจองเฉพาะวันพฤหัสบดี ศุกร์ และเสาร์\nยังไม่ได้บันทึกข้อมูล กรุณาเลือกวันที่รับจอง";
  }
  if (reason === "booking-conflict") {
    return "ไม่สามารถใช้รอบจองในวันที่เลือกได้ ยังไม่ได้บันทึกข้อมูล\nกรุณาตรวจสอบปฏิทินการจอง หรือเลือกวันที่มีห้องว่างวันอื่น";
  }
  if (reason === "booking-has-hn") {
    return "รายการจองนี้มี HN แล้ว กรุณาเปลี่ยนวันที่ในปฏิทินการจอง (Booking Calendar)";
  }
  if (error && /[\u0E00-\u0E7F]/.test(error.message || "")) return error.message;
  return "บันทึกหรือโหลดข้อมูลผู้ป่วยไม่สำเร็จ\nกรุณาตรวจสอบรายการผู้ป่วยและห้องว่างก่อนลองอีกครั้ง";
};

// Booking Calendar interprets Firestore timestamps as Bangkok calendar dates.
export const incomingBookingDay = (value) => {
  if (value && typeof value.toDate === "function") value = value.toDate();
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : new Date(value.getTime() + 7 * 60 * 60 * 1000).toISOString().slice(0, 10);
  }
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : "";
};

export const summarizeIncomingAvailability = (bookings, customer = "CHG03") => {
  const days = {};
  const policy = getIncomingBookingPolicy(customer);
  if (!policy) return days;
  bookings.forEach((booking) => {
    const props = booking.extendedProps || {};
    if (props.status === "Cancel") return;
    const bookingPolicy = getIncomingBookingPolicy(props.customer);
    // CHG11 reserves are selected by order alone; the save sets their customer.
    if (!text(props.order).toUpperCase().startsWith(policy.orderPrefix) ||
        (policy.customer !== "CHG11" && (!bookingPolicy || bookingPolicy.customer !== policy.customer))) return;
    const day = incomingBookingDay(booking.start);
    if (!isIncomingBookingDayAllowed(day, customer)) return;
    if (!days[day]) days[day] = { total: 0, occupied: 0, available: 0 };
    days[day].total += 1;
    // A partially entered name is occupied too; both fields must be blank.
    if (text(props.firstname) || text(props.lastname)) days[day].occupied += 1;
  });
  Object.values(days).forEach((day) => {
    day.available = Math.max(0, Math.min(policy.maxDaily, day.total) - day.occupied);
  });
  return days;
};
